import React, { useState, useRef, useEffect } from "react";
import { FiUser, FiCalendar, FiClock, FiFileText } from 'react-icons/fi';
import AppointmentService from '../../../../AppointmentTimeline/services/appointmentTimelineService';
import { API_DOCTOR_LIST } from "../../../../../helpers/urlHelper";

const AddAppointmentTreatment = ({ 
  isOpen, 
  onClose, 
  patientData, 
  onAppointmentCreated,
  isSubmitting,
  setIsSubmitting 
}) => {
  
  // Initialize the service instance
  const appointmentService = new AppointmentService();

  // State for doctors
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);

  // Initial state with patient already assigned
  const getInitialAppointmentState = () => ({
    userid: patientData?.patientId || '',
    patientName: patientData?.patientName || '',
    patientAvatar: patientData?.patientAvatar || '',
    meetinguserid: '',
    doctorName: '',
    doctorRole: '',
    dateOfBirth: patientData?.dateOfBirth || '',
    date: '',
    startTime: '',
    endTime: '',
    address: patientData?.address || '',
    medicalHistory: '',
    appointmentType: 'Consultation',
    notes: ''
  });

  const [appointment, setAppointment] = useState(getInitialAppointmentState());
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Appointment types
  const APPOINTMENT_TYPES = [
    'Consultation',
    'Check-up',
    'Follow-up',
    'Emergency',
    'Vaccination',
    'Laboratory',
    'Radiology',
    'Surgery',
    'Physical Therapy',
    'Mental Health'
  ];

  // Time slots (every 30 minutes from 8:00 to 18:00)
  const TIME_SLOTS = [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
    '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
    '17:00', '17:30', '18:00'
  ];

  // Function to fetch doctors from API
  const fetchDoctors = async () => {
    try {
      const doctorsResponse = await fetch(`${API_DOCTOR_LIST}`);
      if (!doctorsResponse.ok) {
        throw new Error('Failed to fetch doctors');
      }
      const doctorsData = await doctorsResponse.json();
      
      console.log('Raw doctors response:', doctorsData);
      
      const doctorsArray = doctorsData && doctorsData.data ? doctorsData.data : [];
      
      console.log('Processed doctors array:', doctorsArray);
      return doctorsArray;
    } catch (err) {
      console.error('Error fetching doctors:', err);
      return [];
    }
  };

  // Reset form to initial state with patient data
  const resetOffcanvas = () => {
    setAppointment(getInitialAppointmentState());
    setError(null);
    setSuccessMessage(null);
  };
  
  // Load doctors when component mounts or opens
  useEffect(() => {
    if (isOpen) {
      const loadDoctors = async () => {
        try {
          setLoading(true);
          setError(null);
          
          const doctorsData = await fetchDoctors();
          setDoctors(doctorsData);
          
          // Reset form with current patient data
          resetOffcanvas();
          
        } catch (error) {
          console.error('Error loading doctors:', error);
          setError('Failed to load doctors data.');
        } finally {
          setLoading(false);
        }
      };

      loadDoctors();
    }
  }, [isOpen, patientData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAppointment(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDoctorChange = (e) => {
    const selectedDoctorId = e.target.value;
    const selectedDoctor = doctors.find(d => d._id === selectedDoctorId);
    
    setAppointment(prev => ({
      ...prev,
      meetinguserid: selectedDoctorId,
      doctorName: selectedDoctor ? `Dr. ${selectedDoctor.name || ''} ${selectedDoctor.surname || selectedDoctor.lastName || ''}`.trim() : '',
      doctorRole: selectedDoctor ? selectedDoctor.role || 'Doctor' : ''
    }));
  };

  const handleStartTimeChange = (e) => {
    const startTime = e.target.value;
    setAppointment(prev => ({
      ...prev,
      startTime: startTime,
      endTime: calculateEndTime(startTime)
    }));
  };

  const calculateEndTime = (startTime) => {
    if (!startTime) return '';
    
    const [hours, minutes] = startTime.split(':');
    const startDate = new Date();
    startDate.setHours(parseInt(hours), parseInt(minutes), 0);
    
    // Add 30 minutes for default appointment duration
    const endDate = new Date(startDate.getTime() + 30 * 60000);
    
    return endDate.toTimeString().slice(0, 5);
  };

  const validateForm = () => {
    // Clear previous errors
    setError(null);
    
    // Basic validations
    if (!appointment.userid) {
      setError('Patient information is missing');
      return false;
    }
    
    if (!appointment.meetinguserid) {
      setError('Please select a doctor');
      return false;
    }
    
    if (!appointment.date) {
      setError('Please select appointment date');
      return false;
    }
    
    if (!appointment.startTime) {
      setError('Please select start time');
      return false;
    }
    
    if (!appointment.endTime) {
      setError('Please select end time');
      return false;
    }
    
    if (!appointment.appointmentType) {
      setError('Please select appointment type');
      return false;
    }
    
    // Validate that end time is after start time
    if (appointment.startTime >= appointment.endTime) {
      setError('End time must be after start time');
      return false;
    }
    
    // Validate that appointment date is not in the past
    const appointmentDate = new Date(appointment.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (appointmentDate < today) {
      setError('Appointment date cannot be in the past');
      return false;
    }
    
    // Validate that date is not more than 1 year in the future
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
    
    if (appointmentDate > oneYearFromNow) {
      setError('Appointment date cannot be more than one year in the future');
      return false;
    }
    
    // Validate working hours (8:00 - 18:00)
    const startHour = parseInt(appointment.startTime.split(':')[0]);
    const endHour = parseInt(appointment.endTime.split(':')[0]);
    const endMinutes = parseInt(appointment.endTime.split(':')[1]);
    
    if (startHour < 8) {
      setError('Appointments cannot start before 8:00 AM');
      return false;
    }
    
    if (endHour > 18 || (endHour === 18 && endMinutes > 0)) {
      setError('Appointments cannot end after 6:00 PM');
      return false;
    }
    
    // Validate minimum duration (15 minutes)
    const startTime = new Date(`2000-01-01T${appointment.startTime}`);
    const endTime = new Date(`2000-01-01T${appointment.endTime}`);
    const durationMinutes = (endTime - startTime) / (1000 * 60);
    
    if (durationMinutes < 15) {
      setError('Minimum appointment duration is 15 minutes');
      return false;
    }
    
    // Validate maximum duration (4 hours)
    if (durationMinutes > 240) {
      setError('Maximum appointment duration is 4 hours');
      return false;
    }
    
    // Verify that the selected doctor exists in loaded data
    const selectedDoctor = doctors.find(d => d._id === appointment.meetinguserid);
    
    if (!selectedDoctor) {
      setError('The selected doctor is not valid');
      return false;
    }
    
    // Validate text field lengths
    if (appointment.notes && appointment.notes.length > 1000) {
      setError('Notes cannot exceed 1000 characters');
      return false;
    }
    
    if (appointment.medicalHistory && appointment.medicalHistory.length > 2000) {
      setError('Medical history cannot exceed 2000 characters');
      return false;
    }
    
    return true;
  };

  const saveAppointment = async () => {
    setError(null);
    setSuccessMessage(null);
    
    console.log('=== STARTING APPOINTMENT CREATION ===');
    console.log('Appointment data before validation:', appointment);
    
    if (!validateForm()) {
      console.error('Form validation failed');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Verify that doctor exists
      const selectedDoctor = doctors.find(d => d._id === appointment.meetinguserid);
      
      if (!selectedDoctor) {
        throw new Error('The selected doctor was not found');
      }
      
      // Prepare appointment data with additional validation
      const appointmentData = {
        userid: appointment.userid,
        patientName: appointment.patientName,
        patientAvatar: appointment.patientAvatar,
        meetinguserid: appointment.meetinguserid,
        doctorName: appointment.doctorName || `Dr. ${selectedDoctor.name || ''} ${selectedDoctor.surname || selectedDoctor.lastName || ''}`.trim(),
        doctorRole: appointment.doctorRole || selectedDoctor.role || 'Doctor',
        dateOfBirth: appointment.dateOfBirth || null,
        date: appointment.date,
        startTime: appointment.startTime,
        endTime: appointment.endTime,
        address: appointment.address || '',
        medicalHistory: appointment.medicalHistory || '',
        appointmentType: appointment.appointmentType,
        notes: appointment.notes || ''
      };
      
      console.log('Sending appointment data to backend:', JSON.stringify(appointmentData, null, 2));
      
      // The service handles response and errors internally
      // If no error is thrown, it means it was successful
      const result = await appointmentService.createAppointment(appointmentData);
      
      console.log('Appointment created successfully:', result);
      
      setSuccessMessage('Appointment created successfully!');
      
      // Notify parent component about the new appointment
      if (onAppointmentCreated) {
        onAppointmentCreated(result);
      }
      
      // Close the offcanvas after a brief delay
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (error) {
      console.error('Error creating appointment:', error);
      
      // The service handles errors and converts them to Error objects
      let errorMessage = 'An unexpected error occurred. Please try again.';
      
      if (error.message) {
        // If error comes from service, use direct message
        if (error.message.includes('HTTP Error:') || 
            error.message.includes('Invalid JSON response:') ||
            error.message.includes('fetch')) {
          errorMessage = 'Server connection error. Please try again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get current date for date input defaults
  const getCurrentDate = () => {
    const now = new Date();
    return now.toISOString().slice(0, 10);
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <>
      <div className="offcanvas-backdrop fade show" onClick={onClose}></div>
      <div className="offcanvas offcanvas-end show" tabIndex="-1" style={{width: '600px'}}>
        <div className="offcanvas-header">
          <h5 className="offcanvas-title">Add New Appointment</h5>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
            disabled={isSubmitting}
          ></button>
        </div>
        
        <div className="offcanvas-body">
          {loading && (
            <div className="d-flex justify-content-center my-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}
          
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          
          {successMessage && (
            <div className="alert alert-success" role="alert">
              {successMessage}
            </div>
          )}
          
          <div className="appointment-form">
            {/* Patient Information Section (Read-only) */}
            <div className="mb-4">
              <h6 className="text-primary border-bottom pb-2 mb-3 d-flex align-items-center">
                <FiUser className="me-2" />
                Patient Information
              </h6>
              <div className="card bg-light">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <label className="form-label fw-bold">Patient Name</label>
                      <p className="form-control-plaintext mb-0">
                        {appointment.patientName || 'Not specified'}
                      </p>
                    </div>
                    <div className="col-md-6 mb-2">
                      <label className="form-label fw-bold">Patient ID</label>
                      <p className="form-control-plaintext mb-0">
                        {appointment.userid || 'Not specified'}
                      </p>
                    </div>
                    {appointment.dateOfBirth && (
                      <div className="col-md-6 mb-2">
                        <label className="form-label fw-bold">Date of Birth</label>
                        <p className="form-control-plaintext mb-0">
                          {new Date(appointment.dateOfBirth).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {appointment.address && (
                      <div className="col-md-6 mb-2">
                        <label className="form-label fw-bold">Address</label>
                        <p className="form-control-plaintext mb-0">
                          {appointment.address}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Doctor Selection */}
            <div className="mb-4">
              <h6 className="text-primary border-bottom pb-2 mb-3 d-flex align-items-center">
                <FiUser className="me-2" />
                Doctor Selection
              </h6>
              <div className="row">
                <div className="col-12 mb-3">
                  <label htmlFor="meetinguserid" className="form-label">Doctor *</label>
                  <select
                    className="form-select"
                    id="meetinguserid"
                    name="meetinguserid"
                    value={appointment.meetinguserid}
                    onChange={handleDoctorChange}
                    required
                    disabled={loading}
                  >
                    <option value="">Select doctor</option>
                    {doctors.map(doctor => (
                      <option key={doctor._id} value={doctor._id}>
                        Dr. {doctor.name || ''} {doctor.surname || doctor.lastName || ''}
                        {doctor.role && ` - ${doctor.role}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Appointment Details Section */}
            <div className="mb-4">
              <h6 className="text-primary border-bottom pb-2 mb-3 d-flex align-items-center">
                <FiCalendar className="me-2" />
                Appointment Details
              </h6>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="date" className="form-label">Date *</label>
                  <input
                    type="date"
                    className="form-control"
                    id="date"
                    name="date"
                    value={appointment.date}
                    onChange={handleInputChange}
                    min={getCurrentDate()}
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="appointmentType" className="form-label">Type *</label>
                  <select
                    className="form-select"
                    id="appointmentType"
                    name="appointmentType"
                    value={appointment.appointmentType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select type</option>
                    {APPOINTMENT_TYPES.map(type => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="startTime" className="form-label d-flex align-items-center">
                    <FiClock className="me-1" />
                    Start Time *
                  </label>
                  <select
                    className="form-select"
                    id="startTime"
                    name="startTime"
                    value={appointment.startTime}
                    onChange={handleStartTimeChange}
                    required
                  >
                    <option value="">Select start time</option>
                    {TIME_SLOTS.map(time => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="endTime" className="form-label d-flex align-items-center">
                    <FiClock className="me-1" />
                    End Time *
                  </label>
                  <select
                    className="form-select"
                    id="endTime"
                    name="endTime"
                    value={appointment.endTime}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select end time</option>
                    {TIME_SLOTS.map(time => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Medical Information Section */}
            <div className="mb-4">
              <h6 className="text-primary border-bottom pb-2 mb-3 d-flex align-items-center">
                <FiFileText className="me-2" />
                Medical Information
              </h6>
              <div className="row">
                <div className="col-12 mb-3">
                  <label htmlFor="medicalHistory" className="form-label">Medical History</label>
                  <textarea
                    className="form-control"
                    id="medicalHistory"
                    name="medicalHistory"
                    value={appointment.medicalHistory}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Enter relevant medical history..."
                    maxLength="2000"
                  />
                  <small className="text-muted">
                    {appointment.medicalHistory.length}/2000 characters
                  </small>
                </div>
              </div>
            </div>

            {/* Additional Information Section */}
            <div className="mb-4">
              <h6 className="text-primary border-bottom pb-2 mb-3">Additional Information</h6>
              <div className="row">
                <div className="col-12 mb-3">
                  <label htmlFor="notes" className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    id="notes"
                    name="notes"
                    value={appointment.notes}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Enter any additional notes about the appointment..."
                    maxLength="1000"
                  />
                  <small className="text-muted">
                    {appointment.notes.length}/1000 characters
                  </small>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="d-flex gap-2 pt-3 border-top">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary flex-grow-1"
                onClick={saveAppointment}
                disabled={isSubmitting || loading}
              >
                {isSubmitting ? 'Creating...' : 'Create Appointment'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddAppointmentTreatment;